/**
 * Finding the consumer's added quote among their policies.
 *
 * A consumer has at most one added quote: the option they selected in the consumer flow, saved as a
 * policy funding source. It is identified without a new id field: it is the active policy whose stored
 * `sourceQuote` gives the plan's `selectedQuoteKey`. Saved details load back as text, so `sourceQuote`
 * may be a JSON string or an object.
 */
import { consumerQuoteKey, type ConsumerQuote } from './consumerQuotes';

function parseSourceQuote(value: unknown): ConsumerQuote | null {
  if (!value) return null;
  if (typeof value === 'object') return value as ConsumerQuote;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value) as ConsumerQuote;
  } catch {
    return null;
  }
}

function isDeactivated(policy: PolicyFundingSource): boolean {
  const flag: unknown = policy.isDeactivated;
  return flag === true || flag === 'true';
}

/** The consumer quote key a saved policy was built from, or null when it was not built from one. */
export function consumerQuoteKeyOfPolicy(policy: PolicyFundingSource): string | null {
  const quote = parseSourceQuote(policy.sourceQuote);
  if (!quote || !('traditionalLTCQuoteId' in quote || 'insuranceQuoteId' in quote)) return null;
  return consumerQuoteKey(quote);
}

/**
 * Every active policy built from this quote. Normally one at most; more means a write was interrupted
 * after saving the policy, so the server removes the extras rather than counting them.
 */
export function findAddedQuoteFundingSourceIds(
  ltcPolicies: Record<string, PolicyFundingSource> | undefined,
  quoteKey: string | null | undefined,
): string[] {
  if (!quoteKey) return [];
  return Object.entries(ltcPolicies ?? {})
    .filter(([, policy]) => !isDeactivated(policy) && consumerQuoteKeyOfPolicy(policy) === quoteKey)
    .map(([fundingSourceId]) => fundingSourceId);
}

export function findAddedQuoteFundingSourceId(
  ltcPolicies: Record<string, PolicyFundingSource> | undefined,
  selectedQuoteKey: string | null | undefined,
): string | null {
  return findAddedQuoteFundingSourceIds(ltcPolicies, selectedQuoteKey)[0] ?? null;
}
